/**
 * WinFlash — full-table celebration overlay that fires whenever a hand/roll/spin
 * pays out a positive net. Shared across Craps, Blackjack, Roulette, and 3-Card
 * Poker so every win feels identical and unmistakably rewarding:
 *
 *   ├─ Golden radial glow that pulses across the whole viewport
 *   ├─ "+$X.XX" mega counter that scales in with a bouncy spring
 *   ├─ Ray-burst sparks radiating from the center
 *   └─ Small "WINNER" label above the amount
 *
 * Auto-dismisses after `duration` ms (default 1800). Non-interactive
 * (pointer-events: none) so it never blocks the underlying UI.
 *
 * Usage:
 *   const [win, setWin] = useState(null);
 *   // after a win:
 *   setWin({ amount: 42.50, key: game.id });
 *   // in render:
 *   <WinFlash win={win} onDone={() => setWin(null)} />
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo } from 'react';

const RAYS = 12;
const CONFETTI_COUNT = 46;
const CONFETTI_COLORS = ['#FFD700', '#FF8C00', '#FF3B30', '#39FF14', '#00E1FF', '#F472B6', '#FFF9C4'];
const BIG_WIN_THRESHOLD = 100;       // wins >= $100 get the confetti-rain tier
const MONSTER_WIN_THRESHOLD = 500;   // wins >= $500 get screen-shake + 3x confetti + JACKPOT label

export default function WinFlash({ win, onDone, duration = 1800, label = 'WINNER' }) {
    const amount = Number(win?.amount || 0);
    const isBig = !!win && amount >= BIG_WIN_THRESHOLD;
    const isMonster = !!win && amount >= MONSTER_WIN_THRESHOLD;
    // Effective duration — bigger tiers linger longer so the confetti has time to fall
    const dur = isMonster ? Math.max(duration, 3800) : isBig ? Math.max(duration, 2600) : duration;
    const confettiCount = isMonster ? CONFETTI_COUNT * 3 : CONFETTI_COUNT;

    // Pre-computed confetti particle configs. Recomputed on each `win.key`
    // change so every big win gets a fresh, randomized rain.
    const confetti = useMemo(() => {
        if (!isBig) return [];
        return Array.from({ length: confettiCount }).map((_, i) => ({
            id: i,
            left: Math.random() * 100,               // vw %
            size: 6 + Math.random() * 10,            // px
            color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            rotate: Math.random() * 360,
            spin: (Math.random() > 0.5 ? 1 : -1) * (240 + Math.random() * 360),
            delay: Math.random() * 0.5,
            fallDur: 1.6 + Math.random() * 1.2,
            xDrift: (Math.random() - 0.5) * 40,     // px sideways sway
        }));
    }, [win?.key, isBig, confettiCount]);

    useEffect(() => {
        if (!win) return;
        const t = setTimeout(() => onDone && onDone(), dur);
        return () => clearTimeout(t);
    }, [win, onDone, dur]);

    return (
        <AnimatePresence>
            {win && (
                <motion.div
                    key={win.key || 'win'}
                    initial={{ opacity: 0 }}
                    animate={isMonster
                        ? { opacity: 1, x: [0, -8, 9, -6, 7, -4, 5, -2, 0], y: [0, 4, -5, 3, -3, 2, -2, 1, 0] }
                        : { opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={isMonster
                        ? { opacity: { duration: 0.25 }, x: { duration: 0.7, repeat: 3, ease: 'easeInOut' }, y: { duration: 0.7, repeat: 3, ease: 'easeInOut' } }
                        : { duration: 0.25 }}
                    className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
                    data-testid="win-flash"
                    data-tier={isMonster ? 'monster' : isBig ? 'big' : 'small'}
                >
                    {/* Full-screen soft gold vignette flash */}
                    <motion.div
                        className="absolute inset-0"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 0.6, 0.25, 0.4, 0.15] }}
                        transition={{ duration: dur / 1000, times: [0, 0.12, 0.35, 0.6, 1] }}
                        style={{
                            background: 'radial-gradient(ellipse at center, rgba(255,215,0,0.55) 0%, rgba(255,140,0,0.25) 40%, transparent 75%)',
                        }}
                    />

                    {/* Ray burst — 12 golden spokes radiating out from center */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        {Array.from({ length: RAYS }).map((_, i) => (
                            <motion.div
                                key={i}
                                initial={{ scaleY: 0, opacity: 0 }}
                                animate={{ scaleY: [0, 1, 0.6], opacity: [0, 0.9, 0] }}
                                transition={{ duration: 1.2, delay: 0.05 + i * 0.02, ease: 'easeOut' }}
                                className="absolute origin-bottom"
                                style={{
                                    width: 3,
                                    height: '38vh',
                                    top: '12vh',
                                    background: 'linear-gradient(to top, transparent, #FFD700 60%, #FFF9C4)',
                                    transform: `rotate(${(360 / RAYS) * i}deg) translateY(-8vh)`,
                                    boxShadow: '0 0 12px rgba(255,215,0,0.9)',
                                }}
                            />
                        ))}
                    </div>

                    {/* Confetti rain — big wins only ($100+). Colored strips fall
                        from above the viewport with rotation + sway for a real
                        physical feel. */}
                    {isBig && (
                        <div className="absolute inset-0 overflow-hidden">
                            {confetti.map((c) => (
                                <motion.div
                                    key={c.id}
                                    initial={{ y: '-15vh', x: 0, rotate: c.rotate, opacity: 0 }}
                                    animate={{
                                        y: '115vh',
                                        x: c.xDrift,
                                        rotate: c.rotate + c.spin,
                                        opacity: [0, 1, 1, 0.85],
                                    }}
                                    transition={{
                                        duration: c.fallDur,
                                        delay: c.delay,
                                        ease: [0.35, 0.1, 0.55, 1],
                                        times: [0, 0.05, 0.85, 1],
                                    }}
                                    className="absolute"
                                    style={{
                                        left: `${c.left}vw`,
                                        width: c.size,
                                        height: c.size * 1.6,
                                        background: c.color,
                                        borderRadius: 1,
                                        boxShadow: `0 0 6px ${c.color}80`,
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {/* Center: WINNER label + big +$X.XX amount */}
                    <motion.div
                        className="relative flex flex-col items-center gap-2"
                        initial={{ scale: 0.4, y: 20, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ type: 'spring', damping: 12, stiffness: 260, mass: 0.6 }}
                    >
                        <motion.div
                            className="text-[10px] md:text-xs font-black uppercase tracking-[0.4em] text-[#FFD700]"
                            animate={{ opacity: [1, 0.6, 1] }}
                            transition={{ duration: 0.9, repeat: Infinity }}
                            style={{ textShadow: '0 0 10px rgba(255,215,0,0.8)' }}
                        >
                            {label !== 'WINNER' ? label : (isMonster ? 'JACKPOT' : isBig ? 'BIG WIN' : label)}
                        </motion.div>
                        <motion.div
                            className="text-6xl md:text-8xl font-black font-mono tabular-nums"
                            animate={{ scale: [1, 1.06, 1] }}
                            transition={{ duration: 0.85, repeat: Infinity, ease: 'easeInOut' }}
                            style={{
                                color: '#FFF9C4',
                                textShadow: '0 0 20px rgba(255,215,0,1), 0 0 42px rgba(255,140,0,0.85), 0 4px 0 rgba(0,0,0,0.5)',
                                WebkitTextStroke: '2px #FFD700',
                            }}
                            data-testid="win-flash-amount"
                        >
                            +${Number(win.amount || 0).toFixed(2)}
                        </motion.div>
                        <motion.div
                            className="text-xs md:text-sm font-bold text-[#FFF9C4]/80"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.35 }}
                        >
                            Credited to your wallet
                        </motion.div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
